import { Component, inject, OnInit, ViewChild } from '@angular/core';
import { BehaviorSubject, firstValueFrom, Subscription } from 'rxjs';
import { CustomModalComponent } from 'src/app/components/custom-modal/custom-modal.component';
import { TableAction } from 'src/app/interfaces/custom-table/custom-table-action.interface';
import { ConceptInvoicingModel } from 'src/app/models/invoicing/concept-invoicing.model';
import { ColumnResponseModel } from 'src/app/models/responses/column-response.model';
import { PagedResponseModel } from 'src/app/models/responses/paged-response.model';
import { ConceptInvoicingTableModel } from 'src/app/models/tables/invoicing/concept-invoicing-table.model';
import { ConceptInvoicingService } from 'src/app/services/invoicing/concept-invoicing.service';
import { RegisterConceptInvoicingComponent } from '../../../invoicing/concept-invoicing/register-concept-invoicing/register-concept-invoicing.component';

@Component({
  selector: 'app-concept-invoicing-for-accounting-model',
  templateUrl: './concept-invoicing-for-accounting-model.component.html',
  styleUrls: ['./concept-invoicing-for-accounting-model.component.scss'],
  standalone: false
})
export class ConceptInvoicingForAccountingModelComponent implements OnInit {

  private readonly conceptInvoicingService = inject(ConceptInvoicingService);

  @ViewChild('conceptInvoicingForAccountingModelModal') conceptInvoicingForAccountingModelModalComponent?: CustomModalComponent | null = null;

  ascendant: boolean = true;
  columnSort: string | null = null;
  componentRegisterConceptInvoicing = RegisterConceptInvoicingComponent;
  data: PagedResponseModel<ConceptInvoicingTableModel> = new PagedResponseModel<ConceptInvoicingTableModel>();
  filters: ColumnResponseModel[] = [];
  header: ColumnResponseModel[] = [];
  inputsForConceptInvoicing$ = new BehaviorSubject<any>({
    conceptInvoicingModel: new ConceptInvoicingModel(),
  });
  limit: number = 10;
  page: number = 1;
  subscription: Subscription;
  tableActions: TableAction[] = [
    {
      label: 'Editar',
      icon: 'pi pi-pencil',
      action: 'edit'
    },
  ];
  url: string = 'accounting-model-concept-invoicing';

  async ngOnInit() {
    const filters = localStorage.getItem(`${this.url}`);
    if (filters) this.filters = JSON.parse(filters);
    await Promise.all([
      this.getColumnsForGeneralConsultationForAccountingModel(),
      this.getAllConceptInvoicingsForAccountingModelWithPagination(),
    ]);
    this.subscription = this.inputsForConceptInvoicing$.subscribe(async ({ type }) => {
      switch (type) {
        case 'reload':
          await this.getAllConceptInvoicingsForAccountingModelWithPagination();
          break;
        default:
          console.warn('Tipo de evento desconocido:', type);
      }
    });
  }

  async getAllConceptInvoicingsForAccountingModelWithPagination() {
    try {
      this.data = await firstValueFrom(
        this.conceptInvoicingService.getAllConceptInvoicingsForAccountingModelWithPaginationAsync(
          this.page, this.limit, this.columnSort, this.ascendant, this.filters
        )
      );
    } catch (error) {
      console.error('Error al buscar los conceptos de facturacion para modelo contable.', error);
    }
  }

  async getColumnsForGeneralConsultationForAccountingModel() {
    try {
      if (this.header.length === 0) {
        this.header = await firstValueFrom(
          this.conceptInvoicingService.getColumnsForGeneralConsultationForAccountingModelAsync()
        );
      }
    } catch (error) {
      console.error('Error al buscar las columnas para la consulta general de los conceptos de facturacion para modelo contable.', error);
    }
  }

  onAction(event: { action: string, item?: ConceptInvoicingTableModel, selectedItems?: number[] }) {
    if (event.action === 'edit' && event.item) {
      const conceptInvoicingModel = new ConceptInvoicingModel();
      conceptInvoicingModel.conceptInvoicingId = event.item.id;
      this.updateInput(conceptInvoicingModel);
    }
  }

  async onExport() {
    try {
      const response = await firstValueFrom(
        this.conceptInvoicingService.exportGeneralConsultationForAccountingModel(this.filters, this.columnSort, this.ascendant)
      );
      const blob = response;
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'Configuracion Concepto.xlsx';
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error al exportar la consulta de configuración concepto.', error);
    }
  }

  updateInput(conceptInvoicingContract: ConceptInvoicingModel) {
    this.inputsForConceptInvoicing$.next({
      conceptInvoicingModel: conceptInvoicingContract,
    });
    this.conceptInvoicingForAccountingModelModalComponent && this.conceptInvoicingForAccountingModelModalComponent.show();
  }

  async onFilterChange(event: { filters: any }) {
    const filters = event.filters.map((value: any, index: any): any => ({ value, index }))
      .sort((a: any, b: any) => b.index - a.index).map((item: any) => item.value);
    this.filters = this.removeDuplicates(filters);
    this.filters.forEach((filter: any) => this.processFilter(filter));
  }

  async onPageChange(event: { page: number; pageSize: number; sortField?: string; sortOrder: number }) {
    this.page = event.page;
    this.limit = event.pageSize;
    this.columnSort = event.sortField ?? null;
    this.ascendant = event.sortOrder === 1;
    await this.getAllConceptInvoicingsForAccountingModelWithPagination();
  }

  processFilter(filter: any): void {
    const { propertyName, valueFilter, valueAlterFilter } = filter;
    if (valueFilter === null || valueFilter === undefined || valueFilter === '' || valueFilter === 0) {
      this.filters = this.filters.filter(f => f.propertyName !== propertyName);
    } else {
      const existingFilter = this.filters.find(f => f.propertyName === propertyName);
      if (existingFilter) {
        existingFilter.valueFilter = valueFilter;
        existingFilter.valueAlterFilter = valueAlterFilter;
      }
    }
  }

  removeDuplicates(filters: any[]): any[] {
    return filters.filter((value, index, self) =>
      index === self.findIndex((t: any) => t.propertyName === value.propertyName)
    );
  }

}
